import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Body, Foot, Cell, Row } from './TableParts';
import type { PropsOf } from '@emotion/react';
import { css } from '@emotion/react';
import * as tokens from '@tokens';
import { shouldForwardProp } from '@utils/styledProps';
import { Head } from './Head';
import { HeadCell } from './HeadCell';
import { HeadRow } from './HeadRow';
import { ZINDEX } from '@utils/constants';

export interface TableProps extends PropsOf<'table'> {
  /**
   * Styles override.
   */
  className?: string;

  /**
   * Accessible headline for the table.
   * Only visible to screen readers.
   */
  caption: string;

  /**
   * A name for the table. It's used for accessibility.
   */
  tableName: string;

  /**
   * Children of the table.
   */
  children: ReactNode;

  /**
   * Defines if the last column in the table is sticky or not.
   * It will only be visible if the table has overflow that requires horizontal scrolling.
   * Defaults to true.
   */
  withStickyLastColumn?: boolean;

  /**
   * Width for the table.
   * Defaults to 'unset'
   */
  width?: string;
}

/**
 * @private
 */
interface StyledTableProps extends Omit<TableProps, 'caption' | 'tableName'> {
  /**
   * Indicates if the sticky effect should be activated
   * or not. It depends on the current state of the table overflow.
   */
  showStickyLastColumn: boolean;
}

/**
 * Styled table component.
 * This includes some styling to align the columns.
 * It's done in here to avoid a more complex solution to pass down the required
 * props for the alignment.
 *
 * @private
 */
const StyledTable = styled('table')<StyledTableProps>`
  display: table;
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  position: relative;
  isolation: isolate;

  ${({ withStickyLastColumn, showStickyLastColumn }) =>
    withStickyLastColumn &&
    showStickyLastColumn &&
    css`
      & thead th:last-child {
        position: sticky;
        right: 0;
        z-index: 2;
        border-left: 1px solid ${tokens.color.ui_border_light};
      }

      & tbody tr td:last-child {
        position: sticky;
        right: 0;
        z-index: 0;
        border-left: 1px solid ${tokens.color.ui_border_light};
      }

      & td:last-child:before,
      & th:last-child:before {
        content: '';
        height: 100%;
        width: 30px;
        background: linear-gradient(
          270deg,
          ${tokens.BRAND_COLORS.neutral_100} 0%,
          rgba(255, 255, 255, 0) 100%
        );
        position: absolute;
        left: -31px;
        top: 0;
      }
    `}
`;

/**
 * Caption for the table, used for accessibility purposed.
 * @private
 */
const AccessibilityCaption = styled('caption')`
  display: none;
`;

const Container = styled('div', {
  shouldForwardProp: shouldForwardProp(['width']),
})<{ width: string }>`
  max-width: ${({ width }) => width};
  overflow: auto;
`;

const ShadowContainer = styled.div<{ isVisible: boolean }>`
  position: relative;

  ${({ isVisible }) =>
    isVisible &&
    css`
      &:before {
        content: '';
        height: 100%;
        width: 30px;
        background: linear-gradient(
          90deg,
          ${tokens.BRAND_COLORS.neutral_100} 0%,
          rgba(255, 255, 255, 0) 100%
        );
        position: absolute;
        left: 0;
        top: 0;
        z-index: ${ZINDEX.TABLE_SHADOW};
      }
    `}
`;

const ScrollContainer = styled.div<{ isVisible: boolean }>`
  display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};
  overflow-x: auto;
  overflow-y: hidden;
  position: fixed;
  bottom: 0;
  height: 30px;
`;

const Scrollbar = styled.div`
  height: 30px;
`;

/**
 * Table component.
 *
 * Data tables are used to organize and display data efficiently.
 *
 * {@link https://zeroheight.com/76c439891/p/713804-tables Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-table--basic-table Code documentation}
 *
 * @example
 * <Table>
 *     <Table.Head></Table.Head>
 *     <Table.Body></Table.Body>
 * </Table>
 */
export const Table = ({
  caption,
  tableName,
  children,
  withStickyLastColumn = true,
  width = 'unset',
  ...props
}: TableProps): JSX.Element => {
  const tableRef = useRef<HTMLTableElement>();
  const containerRef = useRef<HTMLDivElement>();
  const scrollContainerRef = useRef<HTMLDivElement>();
  const scrollbarRef = useRef<HTMLDivElement>();
  const endOfContainerRef = useRef<HTMLDivElement>();
  const [isRightColStickyActive, setIsRightColStickyActive] =
    useState<boolean>(false);
  const [isLeftShadowVisible, setLeftShadowVisible] = useState<boolean>(false);
  const [isExtraScrollVisible, setIsExtraScrollVisible] =
    useState<boolean>(false);

  useEffect(() => {
    if (tableRef.current && containerRef.current) {
      const tableElement = tableRef.current;
      const observer = new IntersectionObserver(
        ([e]) => {
          const isActive = e.intersectionRatio < 1;
          setIsRightColStickyActive(isActive);

          const lastThs = tableElement.querySelectorAll(
            'thead tr>th:last-child',
          );

          // We're hardcoding some classes here to help us identify
          // when the last column is sticky or not, inside of the
          // StickyHeadRow component.
          if (isActive && !lastThs[0].classList.contains('isSticky')) {
            lastThs[0].classList.add('isSticky');
            lastThs[1].classList.add('isSticky');
          } else {
            lastThs[0].classList.remove('isSticky');
            lastThs[1]?.classList.remove('isSticky');
          }
        },
        { threshold: [1], root: containerRef.current },
      );

      observer.observe(tableElement);

      // unmount
      return function () {
        observer.unobserve(tableElement);
      };
    }
  }, [tableRef, containerRef]);

  // observes the horizontal scroll to know when the left shadow should show up
  useEffect(() => {
    if (tableRef.current && containerRef.current) {
      const tableElement = tableRef.current;

      const firstColumn = tableElement.querySelector('thead tr>th:first-child');
      let observer;

      if (firstColumn) {
        observer = new IntersectionObserver(
          ([e]) => {
            setLeftShadowVisible(e.intersectionRatio !== 1);
          },
          { threshold: [1], root: containerRef.current },
        );

        observer.observe(firstColumn);
      }

      // unmount
      return function () {
        observer && observer.unobserve(firstColumn);
      };
    }
  }, [tableRef, containerRef, children]);

  useEffect(() => {
    if (!endOfContainerRef.current) {
      return;
    }

    // we're keeping track of a dummy div at the end of the
    // container so we can easily know when the default scrollbar
    // is visible or not.
    const endOfTableContainer = endOfContainerRef.current;

    // check when the extra scrollbar is visible
    const observer = new IntersectionObserver(
      ([e]) => {
        setIsExtraScrollVisible(e.intersectionRatio < 1);
      },
      { threshold: [1] },
    );

    // attach observers
    observer.observe(endOfTableContainer);

    // unmount
    return function () {
      observer.unobserve(endOfTableContainer);
    };
  }, [endOfContainerRef]);

  // Extra scrollbar logic
  useEffect(() => {
    if (
      !tableRef.current ||
      !scrollContainerRef.current ||
      !scrollbarRef.current ||
      !containerRef.current
    ) {
      return () => null;
    }

    const scrollContainerElement = scrollContainerRef.current;
    const containerElement = containerRef.current;

    let cancelScrollOnContainerCounter = 0;
    let cancelScrollOnScrollContainerCounter = 0;

    // sync the size of both of the scrollbars
    const syncScrollBarsSizes = () => {
      if (
        !tableRef.current ||
        !scrollbarRef.current ||
        !containerRef.current ||
        !scrollContainerRef.current
      ) {
        return;
      }

      const currentTableMeasures = tableRef.current.getBoundingClientRect();
      scrollbarRef.current.style.width = `${currentTableMeasures.width}px`;

      const currentContainerMeasures =
        containerRef.current.getBoundingClientRect();
      scrollContainerRef.current.style.width = `${currentContainerMeasures.width}px`;
    };

    syncScrollBarsSizes();

    // Set correct initial values
    scrollContainerElement.scrollLeft = containerElement.scrollLeft;

    // sync the scroll of the extra scrollbar with the scroll of the table
    const containerListener = () => {
      if (cancelScrollOnContainerCounter !== 0) {
        cancelScrollOnContainerCounter -= 1;
        return;
      }

      cancelScrollOnScrollContainerCounter += 1;
      scrollContainerElement.scrollLeft = containerElement.scrollLeft;
    };

    // sync the scroll of the table with the extra scrollbar
    const scrollContainerListener = () => {
      if (cancelScrollOnScrollContainerCounter !== 0) {
        cancelScrollOnScrollContainerCounter -= 1;
        return;
      }

      cancelScrollOnContainerCounter += 1;
      containerElement.scrollLeft = scrollContainerElement.scrollLeft;
    };

    // Attach scroll behaviour to both of the scrollbars
    if (isExtraScrollVisible) {
      scrollContainerRef.current.addEventListener(
        'scroll',
        scrollContainerListener,
        { passive: true },
      );
      containerRef.current.addEventListener('scroll', containerListener, {
        passive: true,
      });
    }

    // Sync sizes on window resize
    const resizeObserver = new ResizeObserver(() => {
      syncScrollBarsSizes();
    });
    resizeObserver.observe(containerRef.current);

    // Sync sizes when the table size changes (added/removed columns)
    const tableResizeObserver = new ResizeObserver(() => {
      syncScrollBarsSizes();
    });
    tableResizeObserver.observe(tableRef.current);

    return () => {
      scrollContainerRef.current?.removeEventListener(
        'scroll',
        scrollContainerListener,
      );
      containerRef.current?.removeEventListener('scroll', containerListener);
      resizeObserver.disconnect();
      tableResizeObserver.disconnect();
    };
  }, [
    tableRef,
    scrollContainerRef,
    scrollbarRef,
    containerRef,
    isExtraScrollVisible,
  ]);

  return (
    <ShadowContainer isVisible={isLeftShadowVisible}>
      <Container ref={containerRef} width={width} data-id="dsTableContainer">
        <StyledTable
          {...props}
          role="table"
          aria-describedby={`table-caption-${tableName}`}
          withStickyLastColumn={withStickyLastColumn}
          showStickyLastColumn={isRightColStickyActive}
          ref={tableRef}
        >
          <AccessibilityCaption id={`table-caption-${tableName}`}>
            {caption}
          </AccessibilityCaption>
          {children}
        </StyledTable>
        <div ref={endOfContainerRef} />
        <ScrollContainer
          ref={scrollContainerRef}
          isVisible={isExtraScrollVisible}
          data-testid="ds-table-scroll-container"
        >
          <Scrollbar ref={scrollbarRef} />
        </ScrollContainer>
      </Container>
    </ShadowContainer>
  );
};

Table.Head = Head;
Table.Body = Body;
Table.Foot = Foot;
Table.Row = Row;
Table.HeadCell = HeadCell;
Table.HeadRow = HeadRow;
Table.Cell = Cell;
